import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { Eyebrow } from "../../components/brand";
import SectionHeader from "../../components/shared/SectionHeader";
import ArticleCard from "../../components/shared/ArticleCard";

/**
 * Public Learning Center — CMS-driven.
 * Fetches published, non-archived articles from `/api/public/articles`.
 * Adding a new article via Command Center → Learning Center CMS makes it
 * appear here automatically — no code changes needed.
 *
 * Routing, article URLs (slugs), SEO metadata, page design, and the AURI
 * "Ask About This" nudge on the detail page are all preserved.
 */
export default function LearningCenter() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("all");

  useEffect(() => {
    api.get("/public/articles")
      .then(r => setArticles(Array.isArray(r.data) ? r.data : []))
      .catch(() => setArticles([]))
      .finally(() => setLoading(false));
  }, []);

  const categories = useMemo(
    () => ["all", ...Array.from(new Set(articles.map(a => a.category).filter(Boolean)))],
    [articles],
  );

  const filtered = useMemo(() => {
    const list = category === "all" ? articles : articles.filter(a => a.category === category);
    // Featured-first, then by published_at desc — mirrors backend sort.
    return [...list].sort((a, b) => {
      if ((a.is_featured ? 1 : 0) !== (b.is_featured ? 1 : 0)) return (b.is_featured ? 1 : 0) - (a.is_featured ? 1 : 0);
      return (b.published_at || "").localeCompare(a.published_at || "");
    });
  }, [articles, category]);

  const [featured, ...rest] = filtered;

  return (
    <div data-testid="page-learning-center">
      <section className="bg-altura-ivory py-20">
        <div className="container-editorial max-w-4xl">
          <Eyebrow>Learning Center</Eyebrow>
          <SectionHeader title="Editorial notes for the global investor." subtitle="Long-form writing, market briefings, and buying-process guides — considered, unrushed, without hype." size="lg" className="mt-2" />
        </div>
      </section>

      <section className="bg-altura-beige/40 border-y border-altura-espresso/10 sticky top-[76px] z-40">
        <div className="container-editorial py-4 flex flex-wrap gap-2" data-testid="learning-filters">
          {categories.map((c) => (
            <button key={c} onClick={() => setCategory(c)}
                    className={`rounded-full px-4 py-1.5 text-xs uppercase tracking-widest border transition ${category === c ? "bg-altura-espresso text-altura-ivory border-altura-espresso" : "border-altura-espresso/15 hover:border-altura-gold text-altura-espresso"}`}
                    data-testid={`filter-${c}`}>
              {c === "all" ? "All essays" : c}
            </button>
          ))}
        </div>
      </section>

      {loading && (
        <section className="bg-altura-ivory py-16">
          <div className="container-editorial text-sm text-altura-charcoal/50 italic">Loading essays…</div>
        </section>
      )}

      {!loading && filtered.length === 0 && (
        <section className="bg-altura-ivory py-16">
          <div className="container-editorial text-sm text-altura-charcoal/60">No essays published in this category yet.</div>
        </section>
      )}

      {featured && (
        <section className="bg-altura-ivory py-16">
          <div className="container-editorial">
            <Link to={`/learning/${featured.slug}`} className="grid lg:grid-cols-2 gap-10 items-center group" data-testid="featured-article">
              <div className="aspect-[4/3] overflow-hidden rounded-lg bg-altura-ivory-2">
                {featured.featured_image ? <img src={featured.featured_image} alt={featured.title} className="h-full w-full object-cover transition-transform duration-1000 group-hover:scale-105" /> : null}
              </div>
              <div>
                <Eyebrow className="text-altura-gold-dark">Featured{featured.category ? ` · ${featured.category}` : ""}</Eyebrow>
                <h2 className="mt-3 font-editorial text-4xl text-altura-charcoal leading-tight">{featured.title}</h2>
                <p className="mt-4 text-altura-charcoal/70 leading-relaxed">{featured.excerpt}</p>
                <div className="mt-6 text-sm text-altura-espresso link-underline">Read the essay →</div>
              </div>
            </Link>
          </div>
        </section>
      )}

      <section className="bg-altura-ivory pb-24">
        <div className="container-editorial">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {rest.map((a) => <ArticleCard key={a.slug} article={{ ...a, image: a.featured_image, date: a.published_at }} />)}
          </div>
        </div>
      </section>
    </div>
  );
}
